import { Link } from 'react-router-dom'

interface Feature {
  title: string
  body: string
}

const FEATURES: Feature[] = [
  {
    title: 'Your library, organised',
    body: 'Every movie and series in one place, with rich metadata from TMDB — cast, crew, posters, ratings, genres, release dates.',
  },
  {
    title: 'Cloud sync across devices',
    body: 'Sign in on any device and pick up exactly where you left off. Watchlists, ratings, and progress stay in sync.',
  },
  {
    title: 'Automatic subtitles',
    body: 'Match subtitles to any title straight from OpenSubtitles. No more hunting for the right release or hash.',
  },
  {
    title: 'AI-powered search',
    body: "Can't remember the name of that movie with the spinning top? Describe it and Govu finds it.",
  },
  {
    title: 'Smart recommendations',
    body: 'Personal recommendations built from what you actually watch, not what a streaming service wants to push.',
  },
  {
    title: 'Awards and history',
    body: 'Track winners and nominees across the Oscars, Golden Globes, Cannes, BAFTA and more — all linked to your library.',
  },
]

export function LandingScreen() {
  return (
    <div className="min-h-dvh bg-bg-primary text-text-primary">
      {/* HERO — full viewport, GOOD VIEW HUNTING wordmark behind the copy.
          Solid-filled G, O, V, U spell out GOVU across the three lines;
          the surrounding letters are stroked outlines. Matches the
          desktop app's NewDiscoverView landing mode letterhead exactly
          (see src/renderer/components/views/NewDiscoverView.tsx). */}
      <section className="relative h-dvh min-h-[640px] w-full overflow-hidden bg-bg-primary">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 flex flex-col items-stretch justify-center overflow-hidden px-4"
          style={{ gap: '2vh' }}
        >
          <WordmarkLine letters="GOOD" filled={[0, 1]} sizeVw={26} />
          <WordmarkLine letters="VIEW" filled={[0]} sizeVw={22} />
          <WordmarkLine letters="HUNTING" filled={[1]} sizeVw={18} />
        </div>

        <header className="relative flex items-center justify-between px-6 py-6 max-w-6xl mx-auto">
          <div className="text-xl font-semibold tracking-tight">Govu</div>
          <nav className="flex items-center gap-2 text-sm">
            <Link
              to="/signin"
              className="px-3 py-1.5 text-white/80 hover:text-white"
            >
              Sign in
            </Link>
            <Link
              to="/register"
              className="px-3 py-1.5 bg-accent-primary hover:bg-accent-hover text-white rounded-md font-medium"
            >
              Enter beta
            </Link>
          </nav>
        </header>

        <div className="relative flex flex-col justify-center items-center text-center px-6 max-w-3xl mx-auto h-full -mt-20">
          <div className="inline-block px-3 py-1 mb-6 text-[11px] uppercase tracking-[0.2em] text-white/80 border border-white/30 rounded-full backdrop-blur-sm bg-black/20">
            Closed beta · invite only
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight text-white leading-[1.05] drop-shadow-[0_2px_20px_rgba(0,0,0,0.8)]">
            Your movie library,<br />
            <span className="text-accent-primary">the way it should be.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-white/80 max-w-xl leading-relaxed drop-shadow-[0_1px_10px_rgba(0,0,0,0.8)]">
            A cinematic desktop app for organising, discovering, and enjoying
            your personal collection. Rich metadata, smart search, cloud sync,
            and recommendations that understand your taste.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/register"
              className="px-7 py-3 bg-accent-primary hover:bg-accent-hover text-white rounded-md font-medium shadow-lg shadow-black/40"
            >
              Redeem invite code
            </Link>
            <Link
              to="/signin"
              className="px-7 py-3 border border-white/40 hover:border-white text-white rounded-md font-medium backdrop-blur-sm bg-black/20"
            >
              I already have an account
            </Link>
          </div>
        </div>

        {/* Scroll cue */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50 text-xs tracking-widest uppercase animate-pulse">
          Scroll to learn more ↓
        </div>
      </section>

      {/* FEATURES */}
      <main className="px-6 py-20 max-w-6xl mx-auto">
        <section>
          <h2 className="text-3xl sm:text-4xl font-semibold text-center mb-3">
            Built for people who take their libraries seriously.
          </h2>
          <p className="text-center text-text-secondary max-w-2xl mx-auto mb-14">
            Govu brings together everything you'd want from Plex, Letterboxd
            and Infuse — without any of the streaming subscriptions.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className="p-5 bg-bg-secondary border border-border-primary rounded-lg hover:border-accent-primary/40 transition-colors"
              >
                <h3 className="text-base font-medium text-text-primary mb-2">{f.title}</h3>
                <p className="text-sm text-text-secondary leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="pt-24">
          <div className="max-w-2xl mx-auto text-center p-8 bg-bg-secondary border border-border-primary rounded-xl">
            <h2 className="text-2xl font-semibold mb-3">Ready to try it?</h2>
            <p className="text-text-secondary mb-6">
              Redeem your invite code to create an account. Once you're in,
              you can download the desktop app.
            </p>
            <Link
              to="/register"
              className="inline-block px-6 py-3 bg-accent-primary hover:bg-accent-hover text-white rounded-md font-medium"
            >
              Enter beta
            </Link>
          </div>
        </section>
      </main>

      <footer className="px-6 py-8 max-w-6xl mx-auto text-center text-xs text-text-tertiary border-t border-border-primary space-y-1">
        <div>Govu · a personal media library project</div>
        <div className="text-[11px]">
          Metadata and imagery provided by{' '}
          <a
            href="https://www.themoviedb.org/"
            target="_blank"
            rel="noreferrer"
            className="underline hover:text-text-secondary"
          >
            The Movie Database
          </a>
          .
        </div>
      </footer>
    </div>
  )
}

function WordmarkLine({
  letters,
  filled,
  sizeVw,
}: {
  letters: string
  filled: number[]
  sizeVw: number
}) {
  const filledSet = new Set(filled)
  return (
    <div
      className="flex select-none justify-between w-full"
      style={{
        fontSize: `${sizeVw}vw`,
        fontWeight: 900,
        fontFamily:
          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
        lineHeight: 0.8,
        textTransform: 'uppercase',
      }}
    >
      {letters.split('').map((ch, i) =>
        filledSet.has(i) ? (
          <span
            key={i}
            style={{ WebkitTextFillColor: '#1a1a1a', WebkitTextStrokeWidth: '0px' }}
          >
            {ch}
          </span>
        ) : (
          <span
            key={i}
            className="text-transparent"
            style={{ WebkitTextStrokeWidth: '2px', WebkitTextStrokeColor: '#1a1a1a' }}
          >
            {ch}
          </span>
        )
      )}
    </div>
  )
}
